import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SectionHeader from '@/components/base/SectionHeader';
import { aboutData } from '@/mocks/about';
import useSEOMeta from '@/hooks/useSEOMeta';


export default function AboutPage() {
  useSEOMeta({
    title: '关于我们 - 苍井空人物志 | 品牌介绍、声誉与联系方式',
    description: '苍井空人物志是一个专注于深度解析苍井空人生故事的独立内容平台。了解我们的品牌理念、媒体评价与口碑，获取联系方式。',
    keywords: '苍井空人物志,关于我们,品牌介绍,联系方式,媒体评价,苍井空',
    canonicalUrl: 'https://cangjingkong.cn/about',
  });

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 苍井空人物志',
    description: '苍井空人物志是一个专注于深度解析苍井空人生故事的独立内容平台。',
    url: 'https://cangjingkong.cn/about',
    isPartOf: { '@type': 'WebSite', name: '苍井空人物志', url: 'https://cangjingkong.cn/' },
    about: {
      '@type': 'Organization',
      name: '苍井空人物志',
      description: aboutData.introduction,
      url: 'https://cangjingkong.cn/',
      contactPoint: {
        '@type': 'ContactPoint',
        email: aboutData.contact.email,
        contactType: 'customer service',
        availableLanguage: ['Chinese', 'Japanese'],
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutData.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-8">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '关于我们' },
              ]}
            />
          </div>

          <section className="pb-8">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  关于我们
                </h1>
                <p className="mt-2 text-foreground-600">
                  {aboutData.tagline}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
                <div>
                  <h2 className="text-xl font-semibold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    品牌介绍
                  </h2>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    {aboutData.introduction}
                  </p>
                  <h3 className="text-lg font-semibold text-foreground-950 mt-8 mb-3">我们的使命</h3>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    {aboutData.mission}
                  </p>
                  <h3 className="text-lg font-semibold text-foreground-950 mt-8 mb-3">我们的愿景</h3>
                  <p className="text-foreground-700 leading-relaxed">
                    {aboutData.vision}
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    核心价值观
                  </h2>
                  <div className="flex flex-col gap-4">
                    {aboutData.values.map((val) => (
                      <div key={val.title} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                        <h4 className="text-base font-semibold text-foreground-950">{val.title}</h4>
                        <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{val.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100/70">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <SectionHeader
                title={aboutData.reputation.title}
                subtitle={aboutData.reputation.description}
              />

              <div className="mt-10 md:mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 mb-14">
                {aboutData.reputation.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="text-2xl md:text-3xl font-bold text-primary-500" style={{ fontFamily: 'var(--font-heading)' }}>
                      {stat.value}
                    </div>
                    <div className="mt-1 text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {aboutData.reputation.testimonials.map((tm, index) => (
                  <div key={index} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                    <p className="text-foreground-700 leading-relaxed text-sm">"{tm.content}"</p>
                    <div className="mt-4 pt-4 border-t border-background-200/70">
                      <p className="text-sm font-semibold text-foreground-950">{tm.author}</p>
                      <p className="text-xs text-foreground-500">{tm.source}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <SectionHeader
                title={aboutData.contact.title}
                subtitle={aboutData.contact.description}
              />

              <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
                <div>
                  <div className="flex flex-col gap-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-500 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">电子邮件</h4>
                        <p className="text-sm text-foreground-600 mt-1">{aboutData.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-500 flex-shrink-0">
                        <i className="ri-wechat-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">微信公众号</h4>
                        <p className="text-sm text-foreground-600 mt-1">{aboutData.contact.wechat}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-500 flex-shrink-0">
                        <i className="ri-weibo-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">微博</h4>
                        <p className="text-sm text-foreground-600 mt-1">{aboutData.contact.weibo}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-500 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">办公地址</h4>
                        <p className="text-sm text-foreground-600 mt-1">{aboutData.contact.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-500 flex-shrink-0">
                        <i className="ri-time-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">工作时间</h4>
                        <p className="text-sm text-foreground-600 mt-1">{aboutData.contact.workHours}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <ContactForm />
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100/70">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <SectionHeader
                title="常见问题"
                subtitle="关于苍井空人物志的常见问题解答"
              />

              <div className="mt-10 md:mt-14 flex flex-col gap-4">
                {aboutData.faq.map((item, index) => (
                  <details key={index} className="group rounded-lg bg-background-50 border border-background-200/70 cursor-pointer">
                    <summary className="flex items-center justify-between p-5 text-sm font-semibold text-foreground-950 list-none">
                      {item.question}
                      <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}

function ContactForm() {
  return (
    <form
      data-readdy-form="true"
      action="https://readdy.ai/api/form/d9q5lkee3oq4jqumtgs0"
      method="POST"
      className="flex flex-col gap-4 p-6 rounded-lg bg-background-50 border border-background-200/70"
    >
      <h3 className="text-lg font-semibold text-foreground-950 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
        发送消息
      </h3>

      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
          您的姓名 <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          id="contact-name"
          name="name"
          required
          className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>

      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
          电子邮箱 <span className="text-red-400">*</span>
        </label>
        <input
          type="email"
          id="contact-email"
          name="email"
          required
          className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
          placeholder="请输入您的电子邮箱"
        />
      </div>

      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
          主题
        </label>
        <input
          type="text"
          id="contact-subject"
          name="subject"
          className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
          placeholder="请输入消息主题"
        />
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          消息内容 <span className="text-red-400">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={4}
          maxLength={500}
          className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors resize-none"
          placeholder="请输入您的消息（不超过500字）"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
      </div>

      <div className="hp-field" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden' }}>
        <input
          type="text"
          name="phone_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <button
        type="submit"
        className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
      >
        发送消息
      </button>
    </form>
  );
}